// components/website/CartDrawer.js - Working Quantity Controls
import React from 'react';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'react-toastify';

const CartDrawer = ({ onClose, onCheckout }) => {
  const { cart, removeFromCart, updateQuantity, totalPrice, totalItems } = useCart();
  const navigate = useNavigate();

  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(item.cartKey || `${item.id}_${item.variantId || 'standard'}`);
      toast.success('Item removed from cart');
    } else {
      updateQuantity(item.cartKey || `${item.id}_${item.variantId || 'standard'}`, newQuantity);
    }
  };

  const handleRemoveItem = (item) => {
    removeFromCart(item.cartKey || `${item.id}_${item.variantId || 'standard'}`);
    toast.success('Item removed from cart');
  };

  const handleCheckout = () => {
    onClose();
    onCheckout();
  };

  return (
    <>
      <div className="cart-overlay" onClick={onClose} />
      <div className="cart-drawer">
        <div className="cart-header">
          <span>Shopping Cart ({totalItems} items)</span>
          <button onClick={onClose} className="icon-btn" style={{ background: 'none' }}>
            <X size={18} />
          </button>
        </div>

        <div className="cart-items">
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <ShoppingBag size={48} color="#cbd5e1" />
              <p style={{ marginTop: '12px', color: '#64748b' }}>Your cart is empty</p>
              <button onClick={onClose} style={{ marginTop: '16px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Continue Shopping
              </button>
            </div>
          ) : (
            cart.map((item, index) => {
              const itemKey = item.cartKey || `${item.id}_${item.variantId || 'standard'}`;
              return (
                <div key={itemKey || index} className="cart-item">
                  <div className="cart-item-img">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="cart-item-details">
                    <h4>{item.name}</h4>
                    {item.variantName && item.variantName !== 'Standard' && (
                      <div style={{ fontSize: '0.7rem', color: '#dc2626', marginBottom: '2px' }}>{item.variantName}</div>
                    )}
                    <div className="cart-item-price">₹{item.price?.toLocaleString()}</div>
                    <div className="quantity-controls">
                      <button className="qty-btn" onClick={() => handleQuantityChange(item, item.quantity - 1)}>
                        <Minus size={12} />
                      </button>
                      <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '0.85rem' }}>{item.quantity}</span>
                      <button className="qty-btn" onClick={() => handleQuantityChange(item, item.quantity + 1)}>
                        <Plus size={12} />
                      </button>
                      <button className="remove-btn" onClick={() => handleRemoveItem(item)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>₹{(item.price * item.quantity).toLocaleString()}</div>
                </div>
              );
            })
          )}
        </div>

        {cart.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total">
              <span>Total</span>
              <span style={{ color: '#dc2626', fontSize: '1.2rem' }}>₹{totalPrice.toLocaleString()}</span>
            </div>
            <button className="btn-checkout" onClick={handleCheckout}>
              Proceed to Checkout
            </button>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
              <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Shop with Sugandhi Flowers </span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cart-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          animation: fadeIn 0.2s ease;
        }
        .cart-drawer {
          position: fixed;
          right: 0;
          top: 0;
          height: 100%;
          width: 100%;
          max-width: 400px;
          background: #fff;
          z-index: 2001;
          box-shadow: -4px 0 20px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          animation: slideRight 0.3s ease;
        }
        .cart-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
          font-size: 1rem;
        }
        .cart-items {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
        }
        .cart-item {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f0f0f0;
          align-items: center;
        }
        .cart-item-img {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          background: #f8fafc;
        }
        .cart-item-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .cart-item-details {
          flex: 1;
          min-width: 0;
        }
        .cart-item-details h4 {
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cart-item-price {
          font-weight: 700;
          color: #dc2626;
          margin-bottom: 6px;
          font-size: 0.8rem;
        }
        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .qty-btn {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }
        .qty-btn:hover {
          background: #dc2626;
          color: white;
          border-color: #dc2626;
        }
        .remove-btn {
          background: none;
          color: #ef4444;
          font-size: 0.8rem;
          cursor: pointer;
          border: none;
          padding: 4px;
          display: flex;
          align-items: center;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .remove-btn:hover {
          background: #fee2e2;
        }
        .cart-footer {
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          background: #fff;
        }
        .cart-total {
          display: flex;
          justify-content: space-between;
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 12px;
        }
        .btn-checkout {
          width: 100%;
          padding: 14px;
          background: #dc2626;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-checkout:hover {
          background: #b91c1c;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default CartDrawer;